import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  List,
  Badge,
  Avatar,
  Divider,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
} from "@mui/material";
import { fToNow } from "../../../utils/formatTime";
import Iconify from "../../../components/Iconify";
import Scrollbar from "../../../components/ScrollBar";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../store";
import { setNotifications } from "../../../store/notificationReducer";
import { useNavigate } from "react-router-dom";
import { INotification, SocketUpdateType } from "../../../models";
import Tooltip from "../../../components/Tooltip";

export default function NotificationsPopover() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const notifications = useSelector(
    (state: RootState) => state.notification.list,
  );

  const totalUnRead = notifications.filter(
    (item: any) => item.isUnRead === true,
  ).length;

  const [open, setOpen] = useState(null);

  const handleOpen = (event: any) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = () => {
    dispatch(
      setNotifications(
        notifications.map((notification: any) => ({
          ...notification,
          isUnRead: false,
        })),
      ),
    );
  };

  const handleClick = (notification: INotification, link: string) => {
    dispatch(
      setNotifications(
        notifications.map((n: any) =>
          n.id === notification.id
            ? {
                ...n,
                isUnRead: false,
              }
            : n,
        ),
      ),
    );
    if (link) {
      navigate(link);
    }
  };

  return (
    <>
      <IconButton
        color={open ? "primary" : "default"}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 360,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{t("Notifications")}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("YouhaveNunreadmessages", { count: totalUnRead })}
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title={t("Markallasread")}>
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
          <List
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{ py: 1, px: 2.5, typography: "overline" }}
              >
                {t("New")}
              </ListSubheader>
            }
          >
            {notifications.slice(0, 2).map((notification: any) => (
              <Item
                key={notification.id}
                t={t}
                notification={notification}
                onClick={handleClick}
              />
            ))}
          </List>

          <List
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{ py: 1, px: 2.5, typography: "overline" }}
              >
                {t("Beforethat")}
              </ListSubheader>
            }
          >
            {notifications.slice(2).map((notification: any) => (
              <Item
                key={notification.id}
                t={t}
                notification={notification}
                onClick={handleClick}
              />
            ))}
          </List>
        </Scrollbar>

        {/* <Divider sx={{ borderStyle: "dashed" }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple>
            {t("ViewAll")}
          </Button>
        </Box> */}
      </Popover>
    </>
  );
}

function Item({ t, notification, onClick }: any) {
  // const { avatar, title, description, createdAt, isUnRead } = notification;
  const { avatar, title, description, link } = renderContent(t, notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        ...(notification.isUnRead && {
          bgcolor: "action.selected",
        }),
      }}
      onClick={() => onClick(notification, link)}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: "background.neutral" }}>
          {<Iconify icon={avatar} sx={{ color: "#0d00ff" }} />}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle2">
            {title}
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              &nbsp; {description}
            </Typography>
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              color: "text.disabled",
            }}
          >
            <Iconify
              icon="eva:clock-outline"
              sx={{ mr: 0.5, width: 16, height: 16 }}
            />
            {fToNow(notification.createdAt)}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

function renderContent(t: any, notification: any): any {
  let result = {
    title: t(`n-${notification.type}-title`),
    description: t(`n-common-desc`, {
      by: notification.data.by,
    }),
  };
  switch (notification.type) {
    case SocketUpdateType.CREATE_TABLE:
    case SocketUpdateType.UPDATE_TABLE:
    case SocketUpdateType.DELETE_TABLE:
      return {
        ...result,
        avatar: "material-symbols:table-restaurant",
        title: t(`n-${notification.type}-title`, {
          name: notification.data.name,
        }),
        link: "/restaurant",
      };
    case SocketUpdateType.CREATE_ORDER:
    case SocketUpdateType.UPDATE_ORDER:
    case SocketUpdateType.DELETE_ORDER:
      return {
        ...result,
        avatar: "material-symbols:dine-lamp-outline",
        title: t(`n-${notification.type}-title`, {
          billNumber: notification.data.billNumber,
        }),
        link: "/restaurant",
      };
    case SocketUpdateType.GENERATE_BILL:
      if (notification.data.tableName) {
        return {
          ...result,
          avatar: "material-symbols:dine-lamp-outline",
          title: notification.data.isGenerateBill
            ? t(`n-${notification.type}-title`, {
                billNumber: notification.data.billNumber,
                tableName: notification.data.tableName,
              })
            : t(`n-${notification.type}-back-title`, {
                billNumber: notification.data.billNumber,
                tableName: notification.data.tableName,
              }),
          link: "/restaurant",
        };
      } else {
        return {
          ...result,
          avatar: "material-symbols:dine-lamp-outline",
          title: notification.data.isGenerateBill
            ? t(`n-${notification.type}-pickup-title`, {
                billNumber: notification.data.billNumber,
              })
            : t(`n-${notification.type}-pickup-back-title`, {
                billNumber: notification.data.billNumber,
              }),
          link: "/restaurant",
        };
      }
    case SocketUpdateType.COMPLETE_ORDER:
      return {
        ...result,
        avatar: "material-symbols:dine-lamp-outline",
        title: notification.data.isCleaning
          ? notification.data.tableName
            ? t(`n-${notification.type}-cleaning-title`, {
                billNumber: notification.data.billNumber,
                tableName: notification.data.tableName,
              })
            : t(`n-${notification.type}-pickup-cleaning-title`, {
                billNumber: notification.data.billNumber,
              })
          : t(`n-${notification.type}-title`, {
              billNumber: notification.data.billNumber,
            }),
        link: "/restaurant",
      };
    case SocketUpdateType.UPDATE_ORDER_PRODUCT:
      return {
        ...result,
        avatar: "material-symbols:dine-lamp-outline",
        title: notification.data.isResendRequest
          ? t(`n-${notification.type}-resendRequest-title`, {
              billNumber: notification.data.billNumber,
              productName: notification.data.productName,
              kitchenName: notification.data.kitchenName,
              // tableName: notification.data.tableName,
            })
          : t(`n-${notification.type}-title`, {
              billNumber: notification.data.billNumber,
              productName: notification.data.productName,
              kitchenName: notification.data.kitchenName,
              // tableName: notification.data.tableName,
            }),
      };
    default:
      return result;
  }
}
